import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Lock, CheckCircle2, AlertCircle } from "lucide-react";
import { validateResetToken, resetPassword } from "../lib/adminApi";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [tokenErr, setTokenErr] = useState("");
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    validateResetToken(token)
      .then((data) => { setInfo(data); setChecking(false); })
      .catch((e) => {
        setTokenErr(e?.response?.data?.detail || "Link is invalid or has expired");
        setChecking(false);
      });
  }, [token]);

  const heading = info?.purpose === "invite" ? "Set your password" : "Choose a new password";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("Use at least 8 characters."); return; }
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
      <div className="w-full max-w-sm">
        <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-8">
          {checking ? (
            <p className="text-sm text-[#6B7280]">Checking link…</p>
          ) : tokenErr ? (
            <div data-testid="reset-invalid" className="rounded-sm border border-red-200 bg-red-50 p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-700">Link unavailable</p>
                  <p className="text-xs text-red-600 mt-1">{tokenErr}</p>
                  <button
                    onClick={() => navigate("/admin/forgot-password")}
                    className="mt-3 text-xs font-medium text-[#025979] hover:text-[#03738C]"
                  >
                    Request a new link
                  </button>
                </div>
              </div>
            </div>
          ) : done ? (
            <div data-testid="reset-success" className="rounded-sm border border-[#025979]/20 bg-[#E0F2FE] p-4">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#025979] mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[#025979]">Password saved</p>
                  <p className="text-xs text-[#4B5563] mt-1 leading-relaxed">
                    You can now sign in with your new password.
                  </p>
                  <Button
                    onClick={() => navigate("/transformation")}
                    className="mt-3 h-9 bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
                    data-testid="reset-go-login"
                  >
                    Continue to sign in
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Lock className="w-5 h-5 text-[#025979]" />
                <h1 className="text-xl font-bold text-[#1F2937]" style={{ fontFamily: "Chivo, sans-serif" }}>
                  {heading}
                </h1>
              </div>
              <p className="text-xs text-[#4B5563] mb-5">
                Setting a password for <span className="font-medium text-[#1F2937]">{info?.email}</span>.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-[#1F2937] block mb-1">New password</label>
                  <Input
                    data-testid="reset-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="rounded-sm"
                    required
                    minLength={8}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#1F2937] block mb-1">Confirm password</label>
                  <Input
                    data-testid="reset-confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="rounded-sm"
                    required
                    minLength={8}
                  />
                </div>
                {err && <p data-testid="reset-error" className="text-xs text-red-500">{err}</p>}
                <Button
                  type="submit"
                  data-testid="reset-submit"
                  disabled={submitting}
                  className="w-full bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-sm h-10"
                >
                  {submitting ? "Saving…" : "Save password"}
                </Button>
                <p className="text-[10px] text-[#9CA3AF]">
                  Minimum 8 characters. Use a mix of letters, numbers, and symbols.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
